import {
    Card,
    CardHeader,
    Dropdown,
    Field,
    Input,
    Table,
    TableBody,
    TableCell,
    TableFootNote,
    TableHead,
    TableHeaderCell,
    TableRow,
} from '@/design-system';
import {
    CREDIT_REFERENCE_TYPES,
    CREDIT_TRANSACTION_LABELS,
    CREDIT_TRANSACTION_TYPES,
    type CreditReferenceType,
    type CreditTransactionType,
    type LedgerEntry,
    type LedgerFilters,
} from '@/entities/credit';
import type { Page } from '@/shared/api/envelope';
import { formatDayTimeShort, formatNumber } from '@/shared/lib';
import { Pager } from '@/shared/ui';

import { billingCopy, formatCreditDelta } from '../copy';

export interface LedgerCardProps {
    page: Page<LedgerEntry>;
    filters: LedgerFilters;
    onFiltersChange: (filters: LedgerFilters) => void;
    /** Een andere pagina of een ander filter laadt; de lijst blijft staan. */
    busy?: boolean;
}

/**
 * Het grootboek van het tegoed, in credits.
 *
 * Een financieel logboek: alleen toevoegen. Het saldo is de som van de boekingen,
 * dus hier staat **geen enkele bewerkknop** - een fout herstel je met een
 * tegenboeking, en die komt er als nieuwe regel bij. Filteren kan alleen op wat
 * de API toestaat: type, waar het op geboekt is en de datum.
 */
export function LedgerCard({ page, filters, onFiltersChange, busy = false }: LedgerCardProps) {
    const filtered = Boolean(filters.type || filters.referenceType || filters.from || filters.to);

    return (
        <Card clip as="section" aria-labelledby="ledger-title">
            <CardHeader
                title={<span id="ledger-title">{billingCopy.ledgerTitle}</span>}
                description={billingCopy.ledgerIntro}
            />

            <div className="flex flex-wrap items-end gap-2 border-b border-[var(--border-default)] bg-[var(--surface-card)] px-4 py-3">
                <Field label={billingCopy.filterType} size="sm" className="min-w-[150px]">
                    <Dropdown<CreditTransactionType | ''>
                        value={filters.type ?? ''}
                        options={[
                            { value: '', label: billingCopy.filterTypeAll },
                            ...CREDIT_TRANSACTION_TYPES.map((type) => ({
                                value: type,
                                label: CREDIT_TRANSACTION_LABELS[type],
                            })),
                        ]}
                        onChange={(type) => onFiltersChange({ ...filters, type, page: 1 })}
                    />
                </Field>

                <Field label={billingCopy.filterReference} size="sm" className="min-w-[130px]">
                    <Dropdown<CreditReferenceType | ''>
                        value={filters.referenceType ?? ''}
                        options={[
                            { value: '', label: billingCopy.filterReferenceAll },
                            ...CREDIT_REFERENCE_TYPES.map((type) => ({
                                value: type,
                                label: billingCopy.referenceLabels[type],
                            })),
                        ]}
                        onChange={(referenceType) =>
                            onFiltersChange({ ...filters, referenceType, page: 1 })
                        }
                    />
                </Field>

                <Field label={billingCopy.filterFrom} size="sm" className="min-w-[136px]">
                    <Input
                        type="date"
                        value={filters.from ?? ''}
                        onChange={(event) =>
                            onFiltersChange({ ...filters, from: event.target.value, page: 1 })
                        }
                    />
                </Field>

                <Field label={billingCopy.filterTo} size="sm" className="min-w-[136px]">
                    <Input
                        type="date"
                        value={filters.to ?? ''}
                        onChange={(event) =>
                            onFiltersChange({ ...filters, to: event.target.value, page: 1 })
                        }
                    />
                </Field>
            </div>

            <Table
                columns="118px 116px minmax(0,1fr) 84px 96px"
                minWidth={620}
                label={billingCopy.ledgerTableLabel}
                aria-busy={busy || undefined}
            >
                <TableHead>
                    <TableHeaderCell>{billingCopy.columnDate}</TableHeaderCell>
                    <TableHeaderCell>{billingCopy.columnType}</TableHeaderCell>
                    <TableHeaderCell>{billingCopy.columnDescription}</TableHeaderCell>
                    <TableHeaderCell align="end">{billingCopy.columnCredits}</TableHeaderCell>
                    <TableHeaderCell align="end">{billingCopy.columnBalanceAfter}</TableHeaderCell>
                </TableHead>
                <TableBody>
                    {page.items.map((entry) => (
                        <LedgerRow key={entry.uuid} entry={entry} />
                    ))}
                </TableBody>
            </Table>

            {page.items.length === 0 ? (
                <TableFootNote>
                    {filtered ? billingCopy.ledgerEmptyFiltered : billingCopy.ledgerEmpty}
                </TableFootNote>
            ) : null}

            <Pager
                meta={page.meta}
                noun={billingCopy.ledgerNoun}
                busy={busy}
                onPage={(next) => onFiltersChange({ ...filters, page: next })}
            />
        </Card>
    );
}

function LedgerRow({ entry }: { entry: LedgerEntry }) {
    const context = [
        entry.referenceType ? billingCopy.referenceLabels[entry.referenceType] : null,
        entry.authorName,
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <TableRow>
            <TableCell mono muted>
                {entry.createdAt ? formatDayTimeShort(entry.createdAt) : '—'}
            </TableCell>
            <TableCell>{entry.typeLabel}</TableCell>
            <TableCell>
                <span className="block truncate text-[var(--text-primary)]">
                    {entry.description ?? entry.typeLabel}
                </span>
                {context ? (
                    <span className="block truncate text-[12px] text-[var(--text-tertiary-safe)]">
                        {context}
                    </span>
                ) : null}
            </TableCell>
            <TableCell
                mono
                align="end"
                className={
                    entry.credits > 0 ? 'text-[var(--accent-dark)]' : 'text-[var(--text-primary)]'
                }
            >
                {formatCreditDelta(entry.credits)}
            </TableCell>
            <TableCell mono align="end" muted>
                {formatNumber(entry.balanceAfter)}
            </TableCell>
        </TableRow>
    );
}
